import { HelpOutline } from '@mui/icons-material'
import { Skeleton, Stack, Tooltip } from '@mui/material'
import { GroupOption, DateRangeValue } from '@/types/metrics'
import GroupedBySelector from '@/components/data-visualization/GroupedBySelector'
import OrganizationPickerWithDialog from '@/components/organization-select/OrganizationPickerWithDialog'
import { useOrganizationsContext } from '@/components/organizations/OrganizationsProvider'
import { useRootOrganization } from '@/components/organizations/RootOrganizationProvider'
import TopBarNavigation from '@/components/top-bar-portal/TopBarNavigation'
import DateRangeFilter from './DateRangeFilter'
import { DateRange } from './ProjectMetricsDashboard'

type MetricFiltersBarProps = {
  groupBy: GroupOption
  selectedDateRange: DateRange
  selectedDateRangeValue: DateRangeValue
  selectedOrganizationId: string
  handleUpdateDateRangeData: (
    dateRangeValue: DateRangeValue,
    dateRange: DateRange,
  ) => void
  handleUpdateGroupBy: (groupBy: GroupOption) => void
  handleUpdateOrganizationId: (org: string) => void
}

export default function MetricFiltersBar({
  groupBy,
  selectedDateRange,
  selectedDateRangeValue,
  selectedOrganizationId,
  handleUpdateDateRangeData,
  handleUpdateGroupBy,
  handleUpdateOrganizationId,
}: MetricFiltersBarProps) {
  const { rootOrganization } = useRootOrganization()
  const { organizations, isFetching } = useOrganizationsContext()
  const selectedOrganization = organizations.find(
    (org) => org.id === selectedOrganizationId,
  )
  return (
    <TopBarNavigation isPrimary={false}>
      <Stack
        direction="row"
        gap={1}
        alignItems="center"
        justifyContent="center"
        sx={{ m: 1, mt: 1.5 }}
        flexWrap="wrap"
      >
        {selectedOrganization ? (
          <OrganizationPickerWithDialog
            currentOrganization={selectedOrganization}
            isFetching={isFetching}
            organizations={organizations}
            rootTreeOrgId={rootOrganization.id}
            title="Select an Organization"
            onSave={(org) => {
              handleUpdateOrganizationId(org.id)
            }}
            variant="outlined"
            showRecentOrgs={false}
          />
        ) : (
          <Skeleton variant="rounded" width={150} height={40} />
        )}
        <DateRangeFilter
          selectedDateRange={selectedDateRange}
          selectedDateRangeValue={selectedDateRangeValue}
          handleUpdateDateRangeData={handleUpdateDateRangeData}
        />
        <GroupedBySelector
          groupedBy={groupBy}
          onChange={handleUpdateGroupBy}
          options={['day', 'week', 'month']}
          variant="outlined"
        />
        <Tooltip title="Use these filters to narrow the data to a specific time range and an organization. The organization's descendants will be included in the data.">
          <HelpOutline sx={{ color: 'text.secondary' }} />
        </Tooltip>
      </Stack>
    </TopBarNavigation>
  )
}
